import React, { useState } from "react"
import styles from "./ClinicalDataWide.module.css"
import starReview from "../../assets/images/star_review.svg"
import doctifyLogo from "../../assets/images/doctify-logo.svg"

type DataRow = {
  label: string
  value: string
}

type TabData = {
  id: string
  title: string
  rows: DataRow[]
}

type ClinicalDataWideProps = {
  overline?: string
  heading?: string
  tabs?: TabData[]
}

const defaultTabs: TabData[] = []

function DoctifyRating() {
  return (
    <div className={styles.doctifyBadge}>
      <div className={styles.doctifyStars} aria-label="Rated 5 out of 5">
        {[...Array(5)].map((_, i) => (
          <img key={i} src={starReview} alt="" className={styles.star} />
        ))}
      </div>
      <span className={styles.doctifyRating}>100+ 5-star reviews</span>
      <span className={styles.doctifyDivider} aria-hidden="true" />
      <img src={doctifyLogo} alt="Doctify" className={styles.doctifyLogo} />
    </div>
  )
}

export const ClinicalDataWide = ({
  overline,
  heading = "Clinical Overview",
  tabs = defaultTabs,
}: ClinicalDataWideProps) => {
  const [activeId, setActiveId] = useState(tabs[0]?.id)
  const activeTab = tabs.find(t => t.id === activeId)

  return (
    <section className={styles.section} aria-label="Clinical treatment data">
      <div className={styles.inner}>
        {/* ── Header ───────────────────────────────────────────────────── */}
        <h2 className="sr-only">{heading}</h2>

        {/* ── Tab navigation ───────────────────────────────────────────── */}
        <nav>
          <div
            className={styles.tabNav}
            role="tablist"
            aria-label="Treatment data tabs"
          >
            {tabs.map(tab => (
              <button
                key={tab.id}
                id={`wide-tab-${tab.id}`}
                role="tab"
                aria-selected={activeId === tab.id}
                aria-controls={`wide-panel-${tab.id}`}
                className={`${styles.tabBtn} ${
                  activeId === tab.id ? styles.tabBtnActive : ""
                }`}
                onClick={() => setActiveId(tab.id)}
              >
                {tab.title}
              </button>
            ))}
          </div>
        </nav>

        {/* ── Panels — all in DOM for Google indexability ──────────────── */}
        {tabs.map(tab => (
          <div
            key={tab.id}
            id={`wide-panel-${tab.id}`}
            role="tabpanel"
            aria-labelledby={`wide-tab-${tab.id}`}
            aria-hidden={activeId !== tab.id}
            className={activeId === tab.id ? styles.panel : styles.panelHidden}
          >
            <div className={styles.grid}>
              {tab.rows.map((row, i) => (
                <div key={i} className={styles.cell}>
                  <span className={styles.label}>{row.label}</span>
                  <span className={styles.value}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        ))}

        <p className={styles.footnote}>
          All information is indicative and subject to individual clinical
          assessment by Dr Elizabeth Hawkes. For more detail, see our{" "}
          <a href="#faqs" style={{ color: '#AB8E66', textDecoration: 'underline', fontSize: 'inherit' }}>
            FAQs
          </a>.
        </p>
      </div>
    </section>
  )
}

export default ClinicalDataWide
